import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatCurrency';
import {
  CANONICAL_STAGES,
  CANONICAL_STAGE_LABELS,
  deriveFlag,
  getDaysInMilestone,
  getDaysSinceLastActivity,
  getDaysStale,
  mapToCanonicalStage,
  type CanonicalStage
} from '../../activeLoanModel';
import type { ProspectLoan, ProspectSortConfig } from '../../types';
import type { LoanListStep } from '../loan-list-layout';
import { InitialsAvatar } from '../primitives/InitialsAvatar';
import { ChannelTag } from '../primitives/ChannelTag';
import { LockStatusPill } from '../primitives/LockStatusPill';
import { MilestoneTrack } from '../primitives/MilestoneTrack';
import { RiskFlag } from '../primitives/RiskFlag';
import { ActivityChip } from '../primitives/ActivityChip';
import { ConversationLogHoverCard } from '../primitives/ConversationLogHoverCard';
import { LoanNumberCell } from '../primitives/LoanNumberCell';
import { LoanListCard } from '../primitives/LoanListCard';
import { WRAPPING_PILL_CLASS } from '../primitives/loanRowClasses';

interface HybridDirectionProps {
  prospects: ProspectLoan[];
  isLoading: boolean;
  sortConfig: ProspectSortConfig;
  onSort: (field: string) => void;
  onViewDetails: (prospect: ProspectLoan) => void;
  /**
   * How the view presents itself in the room it has: stage rail beside every
   * column, beside two-line columns, or above one card per loan. The view
   * never scrolls sideways.
   */
  layout?: LoanListStep;
  /** Cards per row in the stacked layout. */
  cardColumns?: 1 | 2;
}

type StageFilter = CanonicalStage | 'all';

const HEADER_CELL_CLASS = 'text-left px-2.5 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground';

// The rail sits beside the loans until the list stacks; `minmax(0, 1fr)` keeps
// the loans column from growing past the room it is given.
const SHELL_CLASS: Record<LoanListStep, string> = {
  wide: 'grid-cols-[260px_minmax(0,1fr)]',
  compact: 'grid-cols-[260px_minmax(0,1fr)]',
  stacked: 'grid-cols-1'
};

export function HybridDirection({
  prospects,
  isLoading,
  onViewDetails,
  layout = 'wide',
  cardColumns = 1
}: HybridDirectionProps) {
  const isStacked = layout === 'stacked';
  const columnCount = layout === 'compact' ? 5 : 8;
  const [stageFilter, setStageFilter] = useState<StageFilter>('all');

  const { byStage, stageCounts, maxStageCount, totalVolume } = useMemo(() => {
    const map = new Map<CanonicalStage, ProspectLoan[]>();
    for (const s of CANONICAL_STAGES) {
map.set(s, []);
}
    let volume = 0;
    for (const loan of prospects) {
      const stage = mapToCanonicalStage(loan);
      map.get(stage)?.push(loan);
      volume += loan.trans_details_loan_amt ?? 0;
    }
    const counts = new Map<CanonicalStage, number>();
    let max = 0;
    for (const [s, list] of map) {
      counts.set(s, list.length);
      if (list.length > max) {
max = list.length;
}
    }
    return { byStage: map, stageCounts: counts, maxStageCount: max, totalVolume: volume };
  }, [prospects]);

  const filtered = useMemo(
    () => (stageFilter === 'all' ? prospects : (byStage.get(stageFilter) ?? [])),
    [stageFilter, prospects, byStage]
  );
  const stalledCount = useMemo(() => filtered.filter(l => deriveFlag(l) !== null).length, [filtered]);
  const avgAmount = filtered.length
    ? filtered.reduce((s, l) => s + (l.trans_details_loan_amt ?? 0), 0) / filtered.length
    : 0;
  const filteredVolume = filtered.reduce((s, l) => s + (l.trans_details_loan_amt ?? 0), 0);

  if (isLoading) {
    return (
      <div className={cn('grid gap-3', SHELL_CLASS[layout])}>
        <div className="rounded-lg border bg-background p-3">
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
          <div className="mt-3 space-y-2">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-6 animate-pulse rounded bg-muted/60" />)}
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-10 animate-pulse rounded-md bg-muted/60" />
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 animate-pulse rounded bg-muted/40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('grid gap-3 items-start', SHELL_CLASS[layout])}>
      {/* Stage rail — beside the loans, or above them once the list stacks */}
      <div className={cn('rounded-lg border bg-background p-3', !isStacked && 'sticky top-4')}>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Pipeline
        </div>
        <div
          className={cn(
            'gap-1.5',
            isStacked ? 'grid grid-cols-[repeat(auto-fit,minmax(min(180px,100%),1fr))]' : 'flex flex-col'
          )}
        >
          <StageRow
            order="·"
            label="All active"
            count={prospects.length}
            max={prospects.length}
            active={stageFilter === 'all'}
            onClick={() => setStageFilter('all')}
          />
          {CANONICAL_STAGES.map((stage, idx) => (
            <StageRow
              key={stage}
              order={String(idx + 1).padStart(2, '0')}
              label={CANONICAL_STAGE_LABELS[stage]}
              count={stageCounts.get(stage) ?? 0}
              max={maxStageCount}
              active={stageFilter === stage}
              onClick={() => setStageFilter(stage)}
            />
          ))}
        </div>
        <div className="mt-4 rounded-md bg-primary/10 p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-primary/80">
            Total volume
          </div>
          <div className="font-mono text-xl font-semibold leading-tight tracking-tight text-primary">
            {formatCurrency(totalVolume)}
          </div>
        </div>
      </div>

      {/* Loans column */}
      <div className="flex min-w-0 flex-col gap-2">
        {/* Summary strip — wraps onto more lines rather than running wide */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-md border bg-background px-3.5 py-2.5 text-[12px]">
          <SummaryItem label="Viewing" value={stageFilter === 'all' ? 'All' : CANONICAL_STAGE_LABELS[stageFilter]} />
          {layout === 'wide' && <Divider />}
          <SummaryItem label="Count" value={filtered.length.toString()} mono />
          {layout === 'wide' && <Divider />}
          <SummaryItem label="Volume" value={formatCurrency(filteredVolume)} mono />
          {layout === 'wide' && <Divider />}
          <SummaryItem label="Avg" value={formatCurrency(avgAmount)} mono />
          {layout === 'wide' && <Divider />}
          <SummaryItem label="Stalled" value={stalledCount.toString()} mono />
        </div>

        {isStacked ? (
          filtered.length === 0 ? (
            <div className="rounded-lg border bg-background py-8 text-center text-[12px] italic text-muted-foreground">
              No loans in this stage
            </div>
          ) : (
            <ul
              role="list"
              aria-label="Loans"
              className={cn('grid gap-2', cardColumns === 2 ? 'grid-cols-2' : 'grid-cols-1')}
            >
              {filtered.map(loan => (
                <li key={loan.id} className="min-w-0">
                  <LoanListCard loan={loan} onOpen={onViewDetails} />
                </li>
              ))}
            </ul>
          )
        ) : (
          <div className="rounded-lg border bg-background overflow-hidden">
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 330px)' }}>
              {/* Fixed layout: column widths are set by the header and the
                  Borrower column takes what is left, so the table always fits.
                  Keyed by layout so a new column set gets a new table: a fixed
                  table reused across column sets can keep stale column widths. */}
              <table key={layout} className="w-full table-fixed text-[12px]">
                <thead className="bg-muted/30 sticky top-0">
                  {layout === 'compact' ? (
                    <tr className="border-b">
                      <th scope="col" className={HEADER_CELL_CLASS}>
                        <span className="block">Borrower</span>
                        <span className="block">Loan #</span>
                      </th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[17%]')}>Amount</th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[24%]')}>
                        <span className="block">Milestone</span>
                        <span className="block">Loan Status</span>
                      </th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[19%]')}>
                        <span className="block">Lock</span>
                        <span className="block">Flag</span>
                      </th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[14%]')}>Officer</th>
                    </tr>
                  ) : (
                    <tr className="border-b">
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[132px]')}>Loan #</th>
                      <th scope="col" className={HEADER_CELL_CLASS}>Borrower</th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[100px]')}>Amount</th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[160px]')}>Milestone</th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[140px]')}>Loan Status</th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[132px]')}>Lock</th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[110px]')}>Officer</th>
                      <th scope="col" className={cn(HEADER_CELL_CLASS, 'w-[116px]')}>Flag</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={columnCount} className="text-center py-8 text-muted-foreground italic">
                        No loans in this stage
                      </td>
                    </tr>
                  ) : (
                    filtered.map(loan => (
                      <HybridRow key={loan.id} loan={loan} onOpen={onViewDetails} compact={layout === 'compact'} />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StageRow({
  order,
  label,
  count,
  max,
  active,
  onClick
}: {
  order: string;
  label: string;
  count: number;
  max: number;
  active: boolean;
  onClick: () => void;
}) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'grid items-center gap-2 rounded-md px-2 py-1.5 text-left transition',
        'grid-cols-[24px_1fr_auto]',
        active
          ? 'bg-primary/15 text-primary'
          : 'text-foreground hover:bg-muted/60'
      )}
    >
      <span className="font-mono text-[10px] text-right text-muted-foreground">{order}</span>
      <span className="text-[12px] font-medium truncate">{label}</span>
      <span
        className={cn(
          'inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-mono',
          active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        )}
      >
        {count}
      </span>
      <span aria-hidden className="col-span-3 mt-1 h-[2px] overflow-hidden rounded-full bg-muted">
        <span
          className="block h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </span>
    </button>
  );
}

function SummaryItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-muted-foreground">{label}:</span>
      <span className={cn('font-semibold text-foreground', mono && 'font-mono')}>{value}</span>
    </div>
  );
}

function Divider() {
  return <span aria-hidden className="inline-block h-4 w-px bg-border" />;
}

function HybridRow({
  loan,
  onOpen,
  compact = false
}: {
  loan: ProspectLoan;
  onOpen: (l: ProspectLoan) => void;
  compact?: boolean;
}) {
  const flag = deriveFlag(loan);
  const days = getDaysInMilestone(loan);
  const daysStale = getDaysStale(loan);
  const daysSinceActivity = getDaysSinceLastActivity(loan);
  const hasConversationLogs = (loan.conversation_logs?.length ?? 0) > 0;
  const borrower = `${loan.borrower_first_name ?? ''} ${loan.borrower_last_name ?? ''}`.trim();
  const officerName = loan.loan_officer_first_name && loan.loan_officer_last_name
    ? `${loan.loan_officer_first_name} ${loan.loan_officer_last_name}`
    : null;
  // Synced verbatim from Encompass CX.LOAN.STATUS; blank sync values count as missing.
  const loanStatus = loan.cx_loan_status?.trim() || null;
  const purposeAndCity = [loan.trans_details_loan_purpose, loan.subject_property_city].filter(Boolean).join(' · ');
  const pillClassName = compact ? WRAPPING_PILL_CLASS : undefined;

  const borrowerControl = (
    <button
      type="button"
      onClick={event => {
        event.stopPropagation();
        onOpen(loan);
      }}
      aria-label={borrower ? undefined : `Open loan ${loan.trans_details_loan_number ?? ''}`.trim()}
      title={borrower || undefined}
      className={cn(
        'block max-w-full rounded-sm text-left font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        compact ? 'break-words leading-snug' : 'truncate'
      )}
    >
      {borrower || '—'}
    </button>
  );

  const loanStatusText = (
    <span className={cn('block whitespace-normal break-words leading-snug', loanStatus ? 'text-foreground' : 'text-muted-foreground')}>
      {loanStatus ?? '—'}
    </span>
  );

  const officer = officerName ? (
    // In the two-line layout the name drops below the avatar before it has to break mid-word.
    <div className={cn('flex items-center gap-1.5 min-w-0', compact && 'flex-wrap')}>
      <InitialsAvatar name={officerName} size={18} />
      <span className={cn('text-[11px]', compact ? 'min-w-0 break-words' : 'truncate')}>
        {loan.loan_officer_first_name}
      </span>
    </div>
  ) : (
    <span className="text-[11px] italic text-muted-foreground">Unassigned</span>
  );

  const flagNode = flag === 'stalled' ? (
    <ConversationLogHoverCard loan={loan} variant="stalled" days={daysStale}>
      <RiskFlag flag="stalled" daysStale={daysStale} className={pillClassName} />
    </ConversationLogHoverCard>
  ) : hasConversationLogs ? (
    <ConversationLogHoverCard loan={loan} variant="active" days={daysSinceActivity}>
      <ActivityChip days={daysSinceActivity} className={pillClassName} />
    </ConversationLogHoverCard>
  ) : (
    <ChannelTag channel={loan.channel} className={pillClassName} />
  );

  if (compact) {
    return (
      <tr
        className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
        onClick={() => onOpen(loan)}
      >
        <td className="px-2.5 py-1.5 align-top">
          <div className="flex items-start gap-1.5 min-w-0">
            <InitialsAvatar name={borrower} size={20} />
            <div className="min-w-0">
              {borrowerControl}
              <LoanNumberCell loanNumber={loan.trans_details_loan_number} wrap />
            </div>
          </div>
        </td>
        <td className="px-2.5 py-1.5 align-top">
          <div className="break-words font-mono text-[12px]">{formatCurrency(loan.trans_details_loan_amt)}</div>
          {purposeAndCity && (
            <div className="break-words text-[10px] leading-snug text-muted-foreground">{purposeAndCity}</div>
          )}
        </td>
        <td className="px-2.5 py-1.5 align-top">
          <MilestoneTrack loan={loan} daysInMilestone={days} compact />
          <div className="mt-1">{loanStatusText}</div>
        </td>
        <td className="px-2.5 py-1.5 align-top">
          <LockStatusPill loan={loan} className={pillClassName} />
          <div className="mt-1">{flagNode}</div>
        </td>
        <td className="px-2.5 py-1.5 align-top">{officer}</td>
      </tr>
    );
  }

  return (
    <tr
      className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
      onClick={() => onOpen(loan)}
      style={{ height: 36 }}
    >
      <td className="px-2.5" onClick={e => e.stopPropagation()}>
        <LoanNumberCell loanNumber={loan.trans_details_loan_number} wrap />
      </td>
      <td className="px-2.5 min-w-0">
        <div className="flex items-center gap-1.5">
          <InitialsAvatar name={borrower} size={20} />
          <div className="min-w-0">
            {borrowerControl}
            {purposeAndCity && (
              <div className="text-[10px] text-muted-foreground truncate">{purposeAndCity}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-2.5 font-mono text-[12px]">
        {formatCurrency(loan.trans_details_loan_amt)}
      </td>
      <td className="px-2.5">
        <MilestoneTrack loan={loan} daysInMilestone={days} compact />
      </td>
      <td className="px-2.5">{loanStatusText}</td>
      <td className="px-2.5">
        <LockStatusPill loan={loan} />
      </td>
      <td className="px-2.5 min-w-0">{officer}</td>
      <td className="px-2.5">{flagNode}</td>
    </tr>
  );
}
